import React, { useRef, useImperativeHandle, useMemo } from "react";
import { cn } from "../../lib/utils";
import { Input } from ".";
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
  FormItemProps,
} from "../form";

type PrefixedInputProps = Omit<React.ComponentProps<"input">, "onChange"> & {
  prefix: string;
  onChange?: (val: string) => void;
  children: React.ReactNode;
};

export const PrefixedInput = React.forwardRef<
  HTMLInputElement,
  PrefixedInputProps
>(({ prefix, onChange, children, ...props }, forwardedRef) => {
  const innerRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(forwardedRef, () => innerRef.current!, []);

  const handleClick = () => {
    innerRef.current?.focus();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(prefix + e.target.value);
  };

  const value = useMemo(() => {
    const val =
      (props.value?.toString() || props.defaultValue?.toString()) ?? "";
    return val.slice(prefix.length);
  }, [props.value, prefix, props.defaultValue]);

  return (
    <div
      className="flex items-center h-9 bg-background border border-input rounded-lg focus-within:ring-2 focus-within:ring-ring"
      onClick={handleClick}
    >
      <div
        className={cn(
          "h-full rounded-l-lg flex items-center",
          props.disabled && "text-muted-foreground/50 bg-muted/50"
        )}
      >
        <div className="px-2 text-sm font-medium leading-[normal] relative after:content-[''] after:w-0.5 after:h-full after:bg-input after:absolute after:right-0">
          {children}
        </div>
      </div>
      <Input
        ref={innerRef}
        {...props}
        className={cn(
          "flex-1 pl-2 py-0 h-full !ring-0 !outline-none rounded-none rounded-r-lg border-none focus-visible:ring-0 focus-visible:ring-offset-0 flex items-center",
          props.className
        )}
        value={value}
        onChange={handleChange}
      />
    </div>
  );
});

PrefixedInput.displayName = "PrefixedInput";

export const FormPrefixedInput = React.forwardRef<
  HTMLInputElement,
  FormItemProps<typeof PrefixedInput, React.ComponentProps<"input">["value"]>
>(({ label, description, className, ...props }, forwardedRef) => {
  return (
    <FormItem className={className}>
      <FormLabel>{label}</FormLabel>
      <FormControl>
        <PrefixedInput {...props} ref={forwardedRef} />
      </FormControl>
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage />
    </FormItem>
  );
});

FormPrefixedInput.displayName = "FormPrefixedInput";
